import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowDownLeft, CheckCircle2, Clock3, Copy, RefreshCw, Wallet, XCircle } from 'lucide-react';
import { getPaymentDashboard, syncPayment } from 'apis/home';
import { EVENTS } from 'common/routes';
import { encodeToBase64 } from 'utils/encodeAndDecode';
import { toast } from 'react-toastify';
import usePaymentNotifications from './usePaymentNotifications';
import useLivePayments, { LIVE_EXIT_MS } from './useLivePayments';

const indiaDate = (daysAgo = 0) => new Date(Date.now() + 330 * 60000 - daysAgo * 86400000).toISOString().slice(0, 10);
const tabs = { live: 'Live activity', today: 'Today’s payments', yesterday: 'Yesterday’s payments', custom: 'Date range' };

const purposes = { plan_upgrade: 'Plan upgrades', event_booking: 'Event bookings', coin_recharge: 'Coin recharges' };
const statusStyles = {
    success: 'border-green-200 bg-green-100 text-green-800', pending: 'border-yellow-200 bg-yellow-100 text-yellow-800',
    failed: 'border-red-200 bg-red-100 text-red-800', cancelled: 'border-gray-300 bg-gray-100 text-gray-700',
    timeout: 'border-orange-200 bg-orange-100 text-orange-800', refunded: 'border-violet-200 bg-violet-100 text-violet-800',
};
const money = (amount, currency) => {
    if (amount == null) return 'Not recorded';
    try {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
    } catch {
        return `${currency || 'Unknown currency'} ${Number(amount).toFixed(2)}`;
    }
};
const time = value => new Date(value).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

const cashfreePaymentUrl = row => {
    // Legacy Razorpay and Apple transactions cannot be opened in Cashfree.
    if (row.apple_transaction_id || !/^\d+$/.test(String(row.paymentId || ''))) return null;
    const params = new URLSearchParams({
        activeNavigation: 'Payments', txId: row.paymentId, orderId: row.orderId,
    });
    return `https://merchant.cashfree.com/merchants/pg/transactions/payments/view?${params}`;
};

function Copyable({ value, label, children }) {
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(String(value));
            toast.success(`${label} copied`);
        } catch {
            toast.error(`Could not copy ${label.toLowerCase()}. Please select and copy it manually.`);
        }
    };
    return <span className="inline-flex items-center gap-2">
        {children || value}
        {value && <button type="button" onClick={copy} aria-label={`Copy ${label.toLowerCase()} ${value}`} title={`Copy ${label.toLowerCase()}`}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
            <Copy size={13} aria-hidden="true" />
        </button>}
    </span>;
}

function Metric({ label, value, detail, icon: Icon, color }) {
    return <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500">{label}</p>
            <span className={`rounded-xl p-2 ${color}`}><Icon size={20} /></span>
        </div>
        <div className="mt-4 text-2xl font-semibold tracking-tight text-gray-900">{value}</div>
        <p className="mt-2 text-xs text-gray-500">{detail}</p>
    </div>;
}

export default function Dashboard() {
    const syncLocks = useRef(new Set());
    const [syncing, setSyncing] = useState(new Set());
    const [syncResults, setSyncResults] = useState({});
    const [loaded, setLoaded] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [tab, setTab] = useState('live');
    const [range, setRange] = useState(null);
    const [draft, setDraft] = useState(() => ({ startDate: indiaDate(6), endDate: indiaDate() }));
    const [rangeError, setRangeError] = useState('');
    const [page, setPage] = useState(1);
    const queryKey = JSON.stringify({ ...(range || {}), page });
    const data = loaded?.key === queryKey ? loaded.data : null;
    const selectTab = value => {
        setTab(value);
        setPage(1);
        setRangeError('');
        const customRange = draft.startDate && draft.endDate && draft.startDate <= draft.endDate
            ? draft : { startDate: indiaDate(6), endDate: indiaDate() };
        setRange(value === 'yesterday' ? { startDate: indiaDate(1), endDate: indiaDate(1) } : value === 'custom' ? customRange : null);
    };
    const applyRange = event => {
        event.preventDefault();
        if (!draft.startDate || !draft.endDate || draft.startDate > draft.endDate) {
            setRangeError('Choose a start date on or before the end date.');
            return;
        }
        setRangeError('');
        setRange({ ...draft });
        setPage(1);
    };
    const refresh = useRef(() => {});
    const notifications = usePaymentNotifications(data, selectTab);
    const syncRow = async row => {
        if (syncLocks.current.has(row._id)) return;
        syncLocks.current.add(row._id);
        setSyncing(new Set(syncLocks.current));
        setSyncResults(value => ({ ...value, [row._id]: null }));
        try {
            const result = await syncPayment(row._id);
            if (!result?.success) throw new Error(result?.response?.data?.message || result?.message || 'Unable to sync payment. Please retry.');
            const message = result.data?.message || 'Payment synced.';
            const review = result.data?.fulfilmentState === 'needs_review';
            setSyncResults(value => ({ ...value, [row._id]: { message, error: review } }));
            if (review) toast.warn(message);
            else toast.success(message);
        } catch (cause) {
            setSyncResults(value => ({ ...value, [row._id]: { message: cause.message, error: true } }));
        } finally {
            await refresh.current();
            syncLocks.current.delete(row._id);
            setSyncing(new Set(syncLocks.current));
        }
    };


    useEffect(() => {
        let active = true;
        let inFlight = false;
        let refreshAgain = false;
        setError(false);
        const fetchData = async () => {
            if (inFlight) { refreshAgain = true; return; }
            inFlight = true;
            setLoading(true);
            try {
                const response = await getPaymentDashboard(JSON.parse(queryKey));
                if (!active) return;
                if (!response?.success || !(response?.data?.summary || response?.data?.today)) throw new Error('Unable to load payments');
                setLoaded({ key: queryKey, data: response.data });
                setError(false);
            } catch {
                if (active) setError(true);
            } finally {
                inFlight = false;
                if (active) setLoading(false);
                if (active && refreshAgain) { refreshAgain = false; fetchData(); }
            }
        };
        refresh.current = fetchData;
        fetchData();
        return () => { active = false; refresh.current = () => {}; };
    }, [queryKey]);

    useEffect(() => {
        if (!autoRefresh && !notifications.enabled) return;
        const id = setInterval(() => {
            if (notifications.enabled || !document.hidden) refresh.current();
        }, 15000);
        const onVisible = () => { if (!document.hidden) refresh.current(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
    }, [autoRefresh, notifications.enabled]);

    const today = data?.summary || data?.today;
    const periodName = tab === 'live' || tab === 'today' ? 'Today' : tab === 'yesterday' ? 'Yesterday' : 'Selected period';
    const periodOrders = periodName === 'Today' ? 'today’s orders' : periodName === 'Yesterday' ? 'yesterday’s orders' : 'orders in this period';
    const periodText = range ? `${range.startDate} – ${range.endDate}` : 'Today';
    const pagination = data?.pagination;
    const statuses = today?.statuses;
    const liveRows = useLivePayments(data, tab === 'live');
    const rows = (tab === 'live' ? liveRows : today?.recent) || [];
    const successRate = today?.total ? `${((statuses.success / today.total) * 100).toFixed(1)}%` : '—';
    const missingAmounts = today?.collections.reduce((sum, row) => sum + row.missingAmount, 0) || 0;
    const placeholder = loading ? '…' : '—';

    return <main className="min-h-screen bg-gray-50 p-4 text-gray-900 md:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-400">Home / Dashboard</p>
                    <h1 className="text-3xl font-semibold tracking-tight">Payments dashboard</h1>
                    <p className="mt-2 text-sm text-gray-500">Live activity and payment performance by date.</p>
                </div>
                <button type="button" onClick={() => refresh.current()} disabled={loading}
                    className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm hover:bg-gray-100 disabled:opacity-50">
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> {loading ? 'Refreshing…' : 'Refresh'}
                </button>
            </header>

            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
                <span>{periodText} · India time (IST)</span>
                <div className="flex flex-wrap items-center gap-4">
                    <button type="button" onClick={notifications.toggle} disabled={notifications.requesting} aria-pressed={notifications.enabled}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                        {notifications.requesting ? 'Requesting permission…' : notifications.enabled ? 'Disable payment notifications' : 'Enable payment notifications'}
                    </button>
                    {data && <span>Last updated {time(data.generatedAt)} IST</span>}
                    <label className="flex cursor-pointer items-center gap-2">
                        <input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} className="accent-emerald-600" />
                        Auto-refresh · 15s
                    </label>
                </div>
            </div>

            <p className="text-xs text-gray-500">{notifications.enabled
                ? 'Payment notifications are on. Checks run every 15 seconds while this dashboard is open, including in a background tab.'
                : 'Enable browser notifications for new payment orders and status changes while this dashboard is open.'}</p>
            {notifications.message && <p role="status" className="text-sm text-amber-700">{notifications.message}</p>}

            {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                Could not refresh payments. {data ? 'Showing the last successful update; figures may be out of date.' : 'Check your connection and try again.'}
                <button type="button" onClick={() => refresh.current()} className="ml-2 font-semibold underline">Retry</button>
            </div>}

            <section aria-label={`${periodName} payment summary`} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Metric label={`${periodName} collections`} icon={Wallet} color="bg-emerald-50 text-emerald-600"
                    value={today ? (today.collections.length ? today.collections.map(row => <div key={row.currency}>{money(row.amount, row.currency)}</div>) : 'No collections') : placeholder}
                    detail="Recorded paid amounts · successful orders" />
                <Metric label="Successful payments" icon={CheckCircle2} color="bg-blue-50 text-blue-600"
                    value={statuses?.success ?? placeholder} detail={`${successRate} of ${periodOrders}`} />
                <Metric label="Pending payments" icon={Clock3} color="bg-amber-50 text-amber-600"
                    value={statuses?.pending ?? placeholder} detail={`${periodName} orders awaiting payment`} />
                <Metric label="Failed payments" icon={XCircle} color="bg-rose-50 text-rose-600"
                    value={statuses?.failed ?? placeholder} detail={`${periodName} orders with a failed status`} />
            </section>

            <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm text-gray-500">
                <span>{periodName} orders <strong className="ml-1 text-gray-900">{today?.total ?? '—'}</strong></span>
                {['cancelled', 'timeout', 'refunded'].map(status => <span key={status} className="capitalize">{status === 'timeout' ? 'Timed out' : status} <strong className="ml-1 text-gray-900">{statuses?.[status] ?? '—'}</strong></span>)}
            </div>
            <p className="text-xs text-gray-500">{range ? `Figures cover orders created from ${range.startDate} through ${range.endDate}, inclusive, in IST` : 'Today’s figures cover orders created from midnight IST'} and reflect their current status. Collections exclude refunds and unsuccessful orders; they are not a settlement report.
                {missingAmounts > 0 && <span className="ml-1 text-amber-700">{missingAmounts} successful order(s) have no recorded paid amount and are excluded from collection totals.</span>}
            </p>

            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 p-5">
                    <div className="min-w-0 flex-1 basis-64">
                        <h2 className="flex items-center gap-2 text-lg font-semibold"><Activity size={19} className="text-emerald-600" /> Payment activity</h2>
                        <p className="mt-1 text-xs text-gray-500">{tab === 'live' ? 'Today’s pending orders · latest 30 by last update.' : range ? 'All orders created in the selected period, including pending payments · newest first.' : 'Today’s completed orders, including successful and failed payments · newest first.'} All times in IST.</p>
                    </div>
                    <div className="ml-auto flex max-w-full flex-wrap justify-end rounded-lg bg-gray-100 p-1" aria-label="Payment activity view">
                        {Object.keys(tabs).map(value => <button key={value} type="button" aria-pressed={tab === value} onClick={() => selectTab(value)}
                            className={`rounded-md px-4 py-2 text-sm font-medium ${tab === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                            {tabs[value]}
                        </button>)}
                    </div>
                </div>
                {tab === 'custom' && <form onSubmit={applyRange} className="flex flex-wrap items-end justify-end gap-3 border-b border-gray-100 p-5">
                    <label className="text-sm text-gray-600">From date
                        <input type="date" required value={draft.startDate} onChange={event => setDraft({ ...draft, startDate: event.target.value })}
                            className="mt-1 block rounded-lg border border-gray-300 px-3 py-2" />
                    </label>
                    <label className="text-sm text-gray-600">To date
                        <input type="date" required value={draft.endDate} min={draft.startDate} onChange={event => setDraft({ ...draft, endDate: event.target.value })}
                            className="mt-1 block rounded-lg border border-gray-300 px-3 py-2" />
                    </label>
                    <button type="submit" className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white">Apply dates</button>
                    {rangeError && <p role="alert" className="w-full text-right text-sm text-red-700">{rangeError}</p>}
                </form>}
                <div className="overflow-x-auto" aria-busy={loading}>
                    <table className="w-full whitespace-nowrap text-left text-sm">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500"><tr>
                            {['Order / Transaction', 'User', 'Purpose', 'Paid amount', 'Status', 'Created (IST)'].map(label => <th key={label} scope="col" className="px-5 py-3 font-medium">{label}</th>)}
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map(row => {
                                const cashfreeUrl = cashfreePaymentUrl(row);
                                const eventUrl = row.method === 'event_booking'
                                    ? row.eventSlug
                                        ? `${EVENTS.VIEW_EVENT}/${encodeURIComponent(encodeToBase64(row.eventSlug))}`
                                        : row.eventId ? `${EVENTS.VIEW_EVENT_BY_ID}/${row.eventId}` : null
                                    : null;
                                const purposeName = row.method === 'plan_upgrade' ? row.planName : row.method === 'event_booking' ? row.eventName : null;
                                const unsupported = !row._id || !String(row.orderId || '').trim() || row.apple_transaction_id
                                    || row.paymentMethod === 'APPLE_IAP' || row.status === 'refunded' || /^pay_/.test(row.paymentId || '');
                                const syncingRow = syncing.has(row._id);
                                return <tr key={row._id} className="hover:bg-gray-50 transition-[opacity,transform] ease-in-out motion-reduce:transition-none"
                                    style={{ transitionDuration: `${LIVE_EXIT_MS}ms`, opacity: row.liveExiting ? 0 : 1, transform: row.liveExiting ? 'translateY(-8px)' : 'translateY(0)' }}>
                                    <td className="px-5 py-4">
                                        <div className="font-medium"><Copyable value={row.orderId} label="Order ID">
                                            {cashfreeUrl ? <a href={cashfreeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" title="Open payment in Cashfree">{row.orderId}</a> : row.orderId}
                                        </Copyable></div>
                                        <div className="mt-1 text-xs text-gray-500"><Copyable value={row.paymentId} label="Transaction ID">{row.paymentId || 'No transaction ID yet'}</Copyable></div>
                                    </td>
                                    <td className="px-5 py-4">
                                        {row.userId ? <Link className="font-medium text-blue-600 hover:underline" to={`/user/details/edit/${row.userId}`} target="_blank" rel="noopener noreferrer">{row.user?.name || 'Name unavailable'}</Link> : <div className="font-medium">User unavailable</div>}
                                        <div className="mt-1 text-xs text-gray-500"><Copyable value={row.user?.email} label="Email">{row.user?.email || 'Email unavailable'}</Copyable></div>
                                        <div className="mt-1 text-xs text-gray-500"><Copyable value={row.user?.phone} label="Mobile">{row.user?.phone || 'Phone unavailable'}</Copyable></div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="max-w-xs whitespace-normal font-medium">{eventUrl ? <Link to={eventUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{row.eventName || 'View event'}</Link> : purposeName || (row.method === 'plan_upgrade' ? 'Plan unavailable' : row.method === 'event_booking' ? 'Event unavailable' : purposes[row.method] || row.method)}</div>
                                        <div className="mt-1 text-xs text-gray-500">{purposes[row.method] || row.method}</div>
                                        <div className="mt-1 text-xs text-gray-400">{row.paymentMethod || '—'}</div>
                                    </td>
                                    <td className="px-5 py-4 font-medium">{row.paid_amount == null ? '—' : money(row.paid_amount, row.currency)}</td>
                                    <td className="px-5 py-4"><span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusStyles[row.status] || 'border-gray-200 bg-gray-100 text-gray-600'}`}><span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />{row.status}</span>
                                        <div className="mt-2 border-t border-gray-200" />
                                        {row.syncedAt && <div className="mt-1 text-xs text-gray-400">
                                            <div>Synced On:</div>
                                            <div>{new Date(row.syncedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                            <div>{new Date(row.syncedAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' })} IST</div>
                                        </div>}
                                        {!['success', 'failed', 'cancelled'].includes(row.status) && <div className="mt-2"><button type="button" onClick={() => syncRow(row)} disabled={unsupported || syncingRow}
                                            aria-label={`Sync payment ${row.orderId || row._id}`} aria-busy={syncingRow}
                                            title={unsupported ? 'Sync requires a Cashfree order. Apple payments and refunds use separate verification flows.' : 'Verify with Cashfree and apply the payment flow'}
                                            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
                                            <RefreshCw size={12} aria-hidden="true" className={syncingRow ? 'animate-spin' : ''} />{syncingRow ? 'Syncing…' : 'Sync'}
                                        </button></div>}
                                        {syncResults[row._id] && <p role={syncResults[row._id].error ? 'alert' : 'status'} className={`mt-2 max-w-xs whitespace-normal text-xs ${syncResults[row._id].error ? 'text-amber-700' : 'text-gray-600'}`}>{syncResults[row._id].message}</p>}
                                    </td>
                                    <td className="px-5 py-4 text-xs text-gray-500">
                                        <div>{time(row.createdAt)}</div>
                                    </td>
                                </tr>;
                            })}
                            {!rows.length && <tr><td colSpan={6} className="px-5 py-14 text-center text-gray-500">
                                <ArrowDownLeft size={26} className="mx-auto mb-3 text-gray-300" />
                                {loading && !data ? 'Loading payments…' : !data ? 'Payment data is unavailable. Use Retry to load it.' : tab === 'live' ? 'No pending payment orders created today.' : range ? 'No payment orders created in this date range.' : 'No completed payment orders created today yet.'}
                            </td></tr>}
                        </tbody>
                    </table>
                </div>
                {tab !== 'live' && pagination && <nav aria-label="Payment pages" className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 px-5 py-4 text-sm">
                    <span>{pagination.total} payments · Page {page} of {Math.max(1, pagination.pages)}</span>
                    <button type="button" disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}
                        className="rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-40">Previous</button>
                    <button type="button" disabled={loading || page >= pagination.pages} onClick={() => setPage(value => value + 1)}
                        className="rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-40">Next</button>
                </nav>}
            </section>

            <section aria-label="Payments by purpose" className="grid gap-4 md:grid-cols-3">
                {Object.entries(purposes).map(([method, label]) => {
                    const item = today?.purposes.find(row => row.method === method);
                    return <div key={method} className="rounded-xl border border-gray-200 bg-white p-5">
                        <h3 className="text-sm font-medium text-gray-500">{label}</h3>
                        <p className="mt-3 text-xl font-semibold">{today ? item?.success || 0 : '—'} <span className="text-xs font-normal text-gray-500">successful</span></p>
                        <p className="mt-1 text-xs text-gray-400">{today ? item?.total || 0 : '—'} {periodName === 'Today' ? 'orders today' : periodName === 'Yesterday' ? 'orders yesterday' : 'orders in this period'}</p>
                    </div>;
                })}
            </section>
        </div>
    </main>;
}
